import { getLogger } from "@core/logging/logger";
import { Store } from "@core/storage/storage";
import { ServerConfig } from "@domain/server-config/server-config";
import { ServerConfigService } from "@domain/server-config/server-config-service";
import { isNotNil } from "@utils/assert";
import { observable } from "micro-observables";
import { Signal } from "micro-signals";

interface ConfigStatus {
	available: boolean;
	error: unknown;
}

export class ServerConfigManager {
	private readonly logger = getLogger("🎛 [ServerConfigManager]");
	private _serverConfig: ServerConfig | null = null;
	private readonly _serverConfigStatus = observable<ConfigStatus>({ available: false, error: null });
	readonly serverConfigStatus = this._serverConfigStatus.readOnly();
	configChanged = new Signal<ServerConfig>();

	get serverConfig(): ServerConfig {
		if (isNotNil(this._serverConfig)) {
			return this._serverConfig;
		}
		throw new Error("Server config does not exist");
	}

	constructor(
		private readonly serverConfigService: ServerConfigService,
		private readonly store: Store<ServerConfig>,
	) {}

	async init(): Promise<void> {
		let config: ServerConfig | undefined;
		let error: unknown = null;

		try {
			config = await this.serverConfigService.getConfig();
			this.store.setItem(config);
		} catch (e) {
			error = e;
			
			// Backup startup: retrieve older config from local storage
			config = this.store.getItem();
		}

		if (isNotNil(config)) {
			this._serverConfig = config;
			this.configChanged.dispatch(config);
		}

		this._serverConfigStatus.set({
			available: isNotNil(config),
			error,
		});
	}

	debugShowNoConfigScreen(error: unknown): void {
		this._serverConfigStatus.set({ available: false, error });
	}
}
